import React, { useState, useEffect } from 'react';
import {
  GraduationCap, BookOpen, School, Trophy, AlertTriangle, Briefcase,
  DollarSign, HelpCircle, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Search, Filter, MapPin, Phone, Mail, Globe, Calendar, Clock, Users,
  CheckCircle, AlertCircle, Info, Star, Award, Building2, FileText,
  Heart, Shield, Zap, Target, ArrowRight, ExternalLink, X, TrendingUp,
  Plane, Banknote, Lightbulb, CreditCard, MessageCircle, Scale
} from 'lucide-react';
import { schools, examResults, scholarships, emergencyAlerts, schoolRankings } from '../lib/supabase';
import { ShowMoreList } from './shared';
import { TOTAL_INSTITUTIONS } from '../lib/schoolDirectory';

// Import educational datasets
import {
  csecSubjects,
  capeSubjects,
  csecByCategory,
  capeByCategory,
  schoolRankings2024,
  topTenSchools,
  allSchools as staticSchoolsList,
  publicSchools,
  independentSchools,
  educationStats,
  subjectCategories
} from '../data/education-data';

// Import scholarships data
import scholarshipsData from '../data/scholarships-jamaica.json';

// ==================== CONSTANTS ====================
const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

const SCHOOL_LEVELS = [
  { id: 'early_childhood', label: 'Early Childhood', icon: '👶' },
  { id: 'primary', label: 'Primary', icon: '📚' },
  { id: 'secondary', label: 'Secondary', icon: '🎓' },
  { id: 'tertiary', label: 'Tertiary', icon: '🏛️' },
  { id: 'tvet', label: 'TVET', icon: '🔧' }
];

const SCHOOL_TYPES = {
  secondary: [
    { id: 'traditional', label: 'Traditional High School' },
    { id: 'technical', label: 'Technical High School' },
    { id: 'upgraded', label: 'Upgraded High School' },
    { id: 'comprehensive', label: 'Comprehensive High School' }
  ],
  primary: [
    { id: 'primary', label: 'Primary School' },
    { id: 'all_age', label: 'All-Age School' },
    { id: 'primary_junior_high', label: 'Primary & Junior High' }
  ]
};

// ==================== BACK BUTTON ====================
const BackButton = ({ goBack, label = "Back" }) => (
  <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// ==================== EMERGENCY ALERT BANNER ====================
export const EmergencyAlertBanner = () => {
  const [alerts, setAlerts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const loadAlerts = async () => {
      const { data } = await emergencyAlerts.getActive();
      setAlerts(data || []);
    };
    loadAlerts();
  }, []);

  if (dismissed || alerts.length === 0) return null;

  const alert = alerts[currentIndex];
  const severityColors = {
    critical: 'bg-red-600',
    warning: 'bg-amber-500',
    info: 'bg-blue-500'
  };

  return (
    <div className={`${severityColors[alert.severity] || 'bg-blue-500'} text-white px-4 py-3 relative`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-bold">{alert.title}</span>
            <span className="mx-2">—</span>
            <span>{alert.message}</span>
            {alert.link_url && (
              <a href={alert.link_url} className="ml-2 underline font-medium" target="_blank" rel="noopener noreferrer">
                {alert.link_text || 'Learn more'}
              </a>
            )}
          </div>
        </div>
        {alerts.length > 1 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentIndex(i => i > 0 ? i - 1 : alerts.length - 1)} className="p-1 hover:bg-white/20 rounded">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm">{currentIndex + 1}/{alerts.length}</span>
            <button onClick={() => setCurrentIndex(i => i < alerts.length - 1 ? i + 1 : 0)} className="p-1 hover:bg-white/20 rounded">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        <button onClick={() => setDismissed(true)} className="p-1 hover:bg-white/20 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ==================== HOME PAGE EDUCATION HERO ====================
// Restyle notes:
// - The stat pills were 3 of 4 identical grey-on-green blobs at text-sm; they
//   now lead with the NUMBER at display size, because the counts (1,000+
//   schools, 14 parishes) are the site's actual proof of substance.
// - Quick-action tiles get real icon tiles, hover lift + press feedback, and a
//   56px minimum height so they work as thumb targets on a phone.
// - The 5-across action grid collapsed to 2 columns on mobile with an orphan
//   in the last row; it is now 1-col → 2 → 5 with the odd tile spanning.
const HERO_ACTIONS = [
  // Not an AI assistant — the askbot matches questions against a curated
  // knowledge base (src/lib/askbotKnowledge.js), so it is labelled as the
  // chatbot it actually is rather than implying a language model.
  { page: 'askbot', icon: MessageCircle, label: 'Ask the Chatbot', hint: 'Answers to common questions' },
  // Points at the whole-country directory, not the High Schools door — the
  // tile says "Search Schools", not "Search High Schools".
  { page: 'all-schools', icon: Search, label: 'Search Schools', hint: `All ${TOTAL_INSTITUTIONS.toLocaleString()} schools` },
  // CSEC only. The index behind every ranking on the site measures one thing:
  // the share of a school's Grade 11 cohort passing five or more CSEC subjects
  // including Maths and English. No CAPE metric exists in the dataset.
  { page: 'rankings', icon: Trophy, label: 'Explore Rankings', hint: 'CSEC pass rates' },
  { page: 'exams', icon: FileText, label: 'Understand Exams', hint: 'PEP, CSEC, CAPE' },
  { page: 'services', icon: Building2, label: 'Find Services', hint: 'Tutors & suppliers' },
];

// The four headline figures. Extracted from EducationHero so the admin-managed
// hero carousel can lay them over a slide image — publishing a slide replaces
// the standing hero, and these numbers are the site's proof of substance, so
// they should survive that swap.
//
// `onImage` adds a scrim. Over the flat brand gradient the -200 label tone is
// fine, but on the slide's CTA row the figures sit over an arbitrary uploaded
// photo, where white text has no guaranteed contrast and the slide's own
// overlay opacity is admin-set and cannot be relied on. `compact` drops the
// max-width and tightens the gaps so four figures fit beside a button.
export const HeroStats = ({ className = '', onImage = false, compact = false }) => (
  <dl
    className={`${
      // `compact` must NOT use a 4-column grid. On the slide's CTA row the
      // container is only as wide as the space left beside the button, so four
      // EQUAL columns end up narrower than their own contents — "1,654" ran
      // into "32" and "Schools" into "CSEC Subjects". Flex sizes each figure to
      // its own width. From `sm` the four sit on ONE line (flex-nowrap) —
      // letting them wrap individually turned the block into a tall narrow
      // column beside the buttons — and when the row cannot fit both, the CTA
      // row drops the whole stats block onto its own line (see HeroCarousel).
      //
      // Below `sm` it must be a 2-column GRID, not that single line. Four
      // non-wrapping figures plus gaps need ~397px, while the carousel's px-16
      // leaves ~230px at 390px wide; the section is overflow-hidden, so
      // "Parishes" was clipped outright on every phone and the whole document
      // scrolled sideways at 320 and 360.
      compact
        ? 'grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:flex-nowrap sm:items-start sm:gap-x-6 sm:gap-y-0'
        : 'grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5 max-w-2xl'
    } ${
      onImage ? 'rounded-2xl bg-black/35 backdrop-blur-sm ring-1 ring-white/15 px-5 py-3.5' : ''
    } ${className}`}
  >
    {[
      // Whole-directory figure (MOEY 2018/19). educationStats.totalSchools is
      // 231 — secondary only — which undercounted the site by 7x under a label
      // that says "Schools".
      { value: TOTAL_INSTITUTIONS.toLocaleString(), label: 'Schools' },
      { value: educationStats.totalCSECSubjects, label: 'CSEC Subjects' },
      { value: educationStats.totalCAPESubjects, label: 'CAPE Subjects' },
      { value: educationStats.parishes.length, label: 'Parishes' },
    ].map((stat) => (
      <div key={stat.label} className={compact ? 'shrink-0' : ''}>
        <dt className="sr-only">{stat.label}</dt>
        <dd>
          <span
            className={`block font-extrabold tabular-nums leading-none ${
              compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'
            }`}
          >
            {stat.value}
          </span>
          <span
            className={`block mt-1.5 text-[13px] font-medium ${
              // nowrap only in the single-line layout. Below `sm` the figures
              // sit in a 2-column grid whose cells are ~80px at 320px wide, so
              // "CSEC Subjects" must be allowed to wrap onto two lines.
              compact ? 'sm:whitespace-nowrap' : ''
            } ${onImage ? 'text-white/85' : 'text-brand-200'}`}
          >
            {stat.label}
          </span>
        </dd>
      </div>
    ))}
  </dl>
);

// The five quick-action tiles. Extracted alongside HeroStats so both survive
// when an admin slide replaces the standing hero — the slide is editorial, but
// these are the site's primary navigation into its own content.
//
// 2-up on phones (5 stacked full-width rows made the hero ~4 screens tall),
// 5-up from lg. The odd fifth tile spans both mobile columns so the grid never
// ends on an orphan.
export const HeroActions = ({ setPage, className = '' }) => (
  <div className={`grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3 ${className}`}>
    {HERO_ACTIONS.map((action, i) => (
      <button
        key={action.page}
        onClick={() => setPage(action.page)}
        className={`group flex flex-col min-h-[56px] p-3.5 sm:p-4 rounded-2xl text-left bg-white/10 ring-1 ring-white/15 backdrop-blur transition-all duration-200 hover:bg-white/20 hover:ring-white/30 hover:-translate-y-0.5 active:scale-[0.98] ${
          i === HERO_ACTIONS.length - 1 ? 'col-span-2 lg:col-span-1' : ''
        }`}
      >
        <span className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/15 ring-1 ring-white/20 mb-2.5 sm:mb-3 transition-transform duration-200 group-hover:scale-110">
          <action.icon className="w-[18px] h-[18px] sm:w-5 sm:h-5" aria-hidden="true" />
        </span>
        <span className="block font-bold text-sm sm:text-[15px] leading-tight">{action.label}</span>
        <span className="block text-xs sm:text-[13px] text-brand-200 mt-1 leading-snug">{action.hint}</span>
      </button>
    ))}
  </div>
);

// The quick actions on the brand ground, for use directly beneath an admin
// slide. The headline figures are NOT here — they ride on the slide's own CTA
// row (see the `aside` prop on HeroCarousel) so the banner carries both the
// promotion and the proof.
export const HeroSupport = ({ setPage }) => (
  <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 text-white shadow-hero mb-8 mt-4">
    <div className="absolute inset-0 bg-hero-sheen pointer-events-none" aria-hidden="true" />
    <div className="relative p-6 sm:p-8 md:p-10">
      <HeroActions setPage={setPage} />
    </div>
  </section>
);

export const EducationHero = ({ setPage }) => (
  <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 text-white shadow-hero mb-8">
    <div className="absolute inset-0 bg-hero-sheen pointer-events-none" aria-hidden="true" />
    <div className="relative p-6 sm:p-8 md:p-12">
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.1] max-w-3xl">
        Understand School in Jamaica
      </h1>
      <p className="mt-4 text-base sm:text-lg md:text-xl text-brand-100 leading-relaxed max-w-2xl">
        Find schools, compare outcomes, plan the next step.
      </p>

      <HeroStats className="mt-8" />

      <HeroActions setPage={setPage} className="mt-9" />
    </div>
  </section>
);

// ==================== FEATURED GUIDES ====================
// Cross-cutting resources relevant to every family regardless of which
// "door" (Primary / High Schools / Universities / Second Chance) applies to
// them. Level-specific guides (PEP, CSEC subjects, HEART, Career Explorer,
// etc.) intentionally live behind their door instead of here — see
// docs/VISUAL_REFRESH.md for the full page-to-door mapping.
export const ForEveryFamilySection = ({ setPage }) => {
  const guides = [
    { id: 'rankings', title: 'School Rankings', desc: 'Academic outcomes, environment & community sentiment', icon: Award, page: 'rankings', color: 'text-brand-600' },
    { id: 'compare-schools', title: 'Compare Schools', desc: 'Side-by-side school comparison', icon: Scale, page: 'compare-schools', color: 'text-brand-600' },
    { id: 'how-school-works', title: 'How School Works', desc: 'A plain-English guide to Jamaica\'s education system', icon: School, page: 'how-school-works', color: 'text-brand-600' },
    { id: 'enrollment', title: 'Enrollment Guide', desc: 'How to register for school', icon: Users, page: 'enrollment', color: 'text-brand-600' },
    { id: 'scholarships', title: 'Scholarships & Funding', desc: 'Grants, JAMVAT, PATH & tertiary funding', icon: DollarSign, page: 'funding', color: 'text-brand-600' },
    { id: 'education-jobs', title: 'Education Jobs', desc: 'Teaching, admin & support roles', icon: Briefcase, page: 'education-jobs', color: 'text-brand-600' },
    { id: 'updates', title: 'News & Updates', desc: 'Disruptions, alerts & announcements', icon: Calendar, page: 'updates', color: 'text-brand-600' },
    { id: 'help', title: 'Help & FAQs', desc: 'Common questions answered', icon: HelpCircle, page: 'help', color: 'text-brand-600' }
  ];

  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-gray-800 mb-4">For Every Family</h2>
      <p className="text-sm text-gray-500 -mt-3 mb-4">Useful no matter which stage your child is at.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {guides.map(guide => (
          // Same hover language as the tiles on the hero band (HeroActions):
          // the surface lifts and brightens, the icon tile brightens with it,
          // the whole card rises 2px. Only the palette differs, because these
          // sit on the light page rather than on navy. Previously the only
          // hover state was shadow-sm -> shadow-md, which on a light grey page
          // is invisible — the cards read as inert.
          <button
            key={guide.id}
            onClick={() => setPage(guide.page)}
            className="group bg-canvas-raised rounded-xl p-5 text-left border border-hairline shadow-sm transition-all duration-200 hover:bg-brand-50/60 hover:border-brand-300 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-50 ring-1 ring-brand-100 mb-3 transition-all duration-200 group-hover:bg-brand-100 group-hover:ring-brand-200 group-hover:scale-110">
              <guide.icon className={`w-6 h-6 ${guide.color}`} aria-hidden="true" />
            </span>
            <h3 className="font-bold text-ink mb-1 transition-colors duration-200 group-hover:text-brand-800">
              {guide.title}
            </h3>
            <p className="text-sm text-ink-muted">{guide.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

// ==================== INTERACTIVE TOOLS SECTION ====================
export const InteractiveToolsSection = ({ setPage }) => {
  const planningTools = [
    {
      id: 'career-path',
      title: 'Career Path Visualiser',
      desc: 'Discover careers that match your interests and strengths',
      icon: Target,
      color: 'from-indigo-500 to-purple-600',
      page: 'career-path'
    },
    {
      id: 'life-path',
      title: 'Life Path Simulator',
      desc: 'Explore different educational journeys and outcomes',
      icon: TrendingUp,
      color: 'from-brand-500 to-cyan-600',
      page: 'life-path'
    }
  ];

  const studentTools = [
    {
      id: 'exam-tracker',
      title: 'Exam Tracker & Study Guide',
      desc: 'Track exam dates, access study resources, prepare for success',
      icon: Calendar,
      color: 'from-blue-500 to-indigo-600',
      page: 'exam-tracker'
    },
    {
      id: 'extracurriculars',
      title: 'Extracurricular Activities',
      desc: 'Clubs, sports, and programs to build your profile',
      icon: Star,
      color: 'from-cyan-500 to-brand-600',
      page: 'extracurriculars'
    }
  ];

  const opportunityTools = [
    {
      id: 'side-hustles',
      title: 'Student Side Hustles',
      desc: 'Safe, legal ways to earn while you learn',
      icon: DollarSign,
      color: 'from-brand-500 to-brand-600',
      page: 'side-hustles'
    },
    {
      id: 'summer-jobs',
      title: 'Summer Employment',
      desc: 'Find summer jobs, internships, and programs',
      icon: Briefcase,
      color: 'from-orange-500 to-amber-600',
      page: 'summer-employment'
    },
    {
      id: 'opportunity-matcher',
      title: 'Opportunity Matcher',
      desc: 'Match your interests with jobs and internships',
      icon: Zap,
      color: 'from-purple-500 to-pink-600',
      page: 'opportunity-matcher'
    }
  ];

  return (
    <div className="mb-8 space-y-6">
      {/* Career Planning */}
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4">Career Planning Tools</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {planningTools.map(tool => (
            <button
              key={tool.id}
              onClick={() => setPage(tool.page)}
              className={`bg-gradient-to-br ${tool.color} rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all`}
            >
              <tool.icon className="w-10 h-10 mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-1">{tool.title}</h3>
              <p className="text-sm opacity-90">{tool.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Student Tools */}
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4">Student Success Tools</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {studentTools.map(tool => (
            <button
              key={tool.id}
              onClick={() => setPage(tool.page)}
              className={`bg-gradient-to-br ${tool.color} rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all`}
            >
              <tool.icon className="w-10 h-10 mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-1">{tool.title}</h3>
              <p className="text-sm opacity-90">{tool.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Opportunities */}
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4">Earn & Learn Opportunities</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {opportunityTools.map(tool => (
            <button
              key={tool.id}
              onClick={() => setPage(tool.page)}
              className={`bg-gradient-to-br ${tool.color} rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all`}
            >
              <tool.icon className="w-8 h-8 mb-2 opacity-90" />
              <h3 className="font-bold mb-1">{tool.title}</h3>
              <p className="text-xs opacity-90">{tool.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== SUPPORT & SAFETY SECTION ====================
export const SupportSafetySection = ({ setPage }) => {
  const resources = [
    {
      id: 'special-ed',
      title: 'Special Education Support',
      desc: 'Autism, learning differences, and accommodations',
      icon: Heart,
      page: 'special-education',
      color: 'text-pink-600',
      bg: 'bg-pink-50'
    },
    {
      id: 'child-protection',
      title: 'Child Protection & Safety',
      desc: 'Reporting abuse, getting help, staying safe',
      icon: Shield,
      page: 'child-protection',
      color: 'text-slate-600',
      bg: 'bg-slate-50'
    }
  ];

  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Support & Safety</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {resources.map(resource => (
          <button
            key={resource.id}
            onClick={() => setPage(resource.page)}
            className={`${resource.bg} rounded-xl p-5 text-left border border-gray-100 hover:shadow-md transition-all`}
          >
            <resource.icon className={`w-8 h-8 ${resource.color} mb-3`} />
            <h3 className="font-bold text-gray-800 mb-1">{resource.title}</h3>
            <p className="text-sm text-gray-600">{resource.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

// ==================== HOW SCHOOL WORKS PAGE ====================
export const HowSchoolWorksPage = ({ setPage, goBack }) => {
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: 'stages',
      title: 'Education Stages',
      icon: GraduationCap,
      content: [
        { stage: 'Early Childhood (Ages 3-5)', desc: 'Basic school, infant school, kindergarten. Focus on socialization and basic skills.' },
        { stage: 'Primary (Ages 6-11)', desc: 'Grades 1-6. Core subjects: Mathematics, Language Arts, Science, Social Studies. Ends with PEP exam.' },
        { stage: 'Secondary (Ages 12-18)', desc: 'Forms 1-6. CSEC exams in Form 5, CAPE in Forms 6. Specialization begins in Form 4.' },
        { stage: 'Tertiary', desc: 'Universities, colleges, HEART/NSTA Trust programs. Degree programs, certificates, diplomas.' },
        { stage: 'TVET', desc: 'Technical and Vocational Education. NVQ-J certifications, practical skills training.' }
      ]
    },
    {
      id: 'school-types',
      title: 'Types of Secondary Schools',
      icon: School,
      content: [
        { stage: 'Traditional High School', desc: 'Academically focused, competitive entry. Strong emphasis on CSEC/CAPE performance.' },
        { stage: 'Technical High School', desc: 'Combines academics with technical/vocational training. Offers NVQ-J alongside CSEC.' },
        { stage: 'Upgraded High School', desc: 'Formerly all-age or junior high schools that now offer full secondary education.' },
        { stage: 'Comprehensive High School', desc: 'Offers both academic and vocational tracks within the same institution.' }
      ]
    },
    {
      id: 'public-private',
      title: 'Public vs Private',
      icon: Building2,
      content: [
        { stage: 'Public Schools', desc: 'Government-funded. Free tuition. Admission often based on PEP scores or zoning.' },
        { stage: 'Private/Preparatory', desc: 'Fee-paying. Often smaller class sizes. Independent curriculum within MOE guidelines.' },
        { stage: 'Grant-Aided', desc: 'Receive government funding but may charge auxiliary fees. Often church-affiliated.' }
      ]
    },
    {
      id: 'governance',
      title: 'Who Runs What',
      icon: Users,
      content: [
        { stage: 'Ministry of Education (MOE)', desc: 'Sets national policy, curriculum standards, and oversees all education.' },
        { stage: 'Regional Offices', desc: '6 education regions manage schools in their areas. Handle teacher placement and support.' },
        { stage: 'School Boards', desc: 'Govern individual schools. Include community members, parents, MOE representatives.' },
        { stage: 'PTA', desc: 'Parent-Teacher Associations support schools through fundraising and community engagement.' }
      ]
    },
    {
      id: 'calendar',
      title: 'School Calendar',
      icon: Calendar,
      content: [
        { stage: 'Term 1 (September - December)', desc: 'Begins early September. Ends mid-December. Christmas break follows.' },
        { stage: 'Term 2 (January - March/April)', desc: 'Begins early January. Ends before Easter. Spring break varies.' },
        { stage: 'Term 3 (April - July)', desc: 'Begins after Easter. Ends late June/early July. Summer break follows.' },
        { stage: 'Exam Period', desc: 'PEP in March. CSEC/CAPE April-June. Results released in August.' }
      ]
    }
  ];

  const faqs = [
    { q: 'What age does school start in Jamaica?', a: 'Children typically start basic/infant school at age 3-4 and primary school at age 6.' },
    { q: 'What is a "traditional high school"?', a: 'An academically-focused secondary school with competitive entry requirements, usually based on PEP scores.' },
    { q: 'Is education free in Jamaica?', a: 'Public school tuition is free. However, parents pay for uniforms, books, transportation, and auxiliary fees.' },
    { q: 'How is high school placement determined?', a: 'Primarily through the PEP exam in Grade 6. Students list school preferences and are placed based on scores and availability.' }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <BookOpen className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">How School Works in Jamaica</h1>
        <p className="text-white/90">A plain-English guide to Jamaica's education system</p>
      </div>

      <div className="space-y-4 mb-8">
        {sections.map(section => (
          <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <section.icon className="w-6 h-6 text-brand-600" />
                <span className="font-bold text-gray-800">{section.title}</span>
              </div>
              {activeSection === section.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            {activeSection === section.id && (
              <div className="px-6 pb-4 space-y-3">
                {section.content.map((item, idx) => (
                  <div key={idx} className="pl-9 border-l-2 border-brand-200 py-2">
                    <h4 className="font-semibold text-gray-800">{item.stage}</h4>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-amber-600" />
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div key={idx}>
              <h3 className="font-semibold text-gray-800">{faq.q}</h3>
              <p className="text-sm text-gray-600 mt-1">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== ENROLLMENT PAGE ====================
export const EnrollmentPage = ({ setPage, goBack }) => {
  const [activeFlow, setActiveFlow] = useState('primary');

  const flows = {
    early_childhood: {
      title: 'Early Childhood Enrollment',
      steps: [
        { title: 'Choose a School', desc: 'Visit basic schools in your area. Consider location, fees, and programs.' },
        { title: 'Contact the School', desc: 'Call or visit to confirm availability and registration dates.' },
        { title: 'Prepare Documents', desc: 'Birth certificate, immunization card, proof of address.' },
        { title: 'Complete Registration', desc: 'Fill out enrollment forms and pay any required fees.' },
        { title: 'Attend Orientation', desc: 'Meet teachers and learn about school policies.' }
      ]
    },
    primary: {
      title: 'Primary School Enrollment',
      steps: [
        { title: 'Determine Your Zone', desc: 'Public schools are zoned. Find your assigned school based on address.' },
        { title: 'Visit During Registration Period', desc: 'Usually January-March for September start.' },
        { title: 'Bring Required Documents', desc: 'Birth certificate (certified), immunization record, proof of address, passport photos.' },
        { title: 'Complete Forms', desc: 'Fill out registration forms. Provide emergency contacts.' },
        { title: 'Purchase Uniforms & Supplies', desc: 'Get uniform from approved vendors. Buy books and supplies.' }
      ]
    },
    secondary: {
      title: 'High School Entry (via PEP)',
      steps: [
        { title: 'Take PEP Exam (Grade 6)', desc: 'Sit the Primary Exit Profile exam in March.' },
        { title: 'Submit School Choices', desc: 'List up to 5 preferred secondary schools in order.' },
        { title: 'Await Placement', desc: 'MOE assigns students based on scores and availability.' },
        { title: 'Accept Placement', desc: 'Visit assigned school during registration period.' },
        { title: 'Complete Enrollment', desc: 'Submit documents, pay fees, purchase uniforms.' }
      ]
    },
    transfer: {
      title: 'School Transfers',
      steps: [
        { title: 'Request Transfer Letter', desc: 'Get official letter from current school stating reason for transfer.' },
        { title: 'Contact New School', desc: 'Confirm they have space and are willing to accept transfer.' },
        { title: 'Submit to MOE (if required)', desc: 'Some transfers require Ministry approval.' },
        { title: 'Provide Documents', desc: 'Academic records, birth certificate, transfer letter.' },
        { title: 'Complete Registration', desc: 'Enroll at new school and notify old school of completion.' }
      ]
    },
    sixth_form: {
      title: 'Sixth Form / Tertiary Entry',
      steps: [
        { title: 'Complete CSEC', desc: 'Pass required subjects (usually 5+ including English and Math).' },
        { title: 'Apply to Sixth Form', desc: 'Some schools require application; others auto-advance.' },
        { title: 'Select CAPE Subjects', desc: 'Choose based on career goals. Get guidance from teachers.' },
        { title: 'Apply to Universities', desc: 'For tertiary, apply through UTECH, UWI, or other institutions.' },
        { title: 'Explore Funding', desc: 'Apply for scholarships, SLB loans, or grants.' }
      ]
    }
  };

  const checklist = {
    documents: [
      'Birth Certificate (certified copy)',
      'Immunization Record',
      'Proof of Address (utility bill)',
      'Passport-sized Photos (2-4)',
      'Previous School Records (if transferring)',
      'PEP Results (for secondary)'
    ],
    questions: [
      'What are the school fees and what do they cover?',
      'What is the class size?',
      'What extracurricular activities are offered?',
      'What support is available for struggling students?',
      'What is the discipline policy?',
      'How does the school communicate with parents?'
    ]
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Users className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Enrollment & Progression</h1>
        <p className="text-white/90">Step-by-step guides for enrolling at every level</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(flows).map(([key, flow]) => (
          <button
            key={key}
            onClick={() => setActiveFlow(key)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeFlow === key
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {flow.title.split(' ')[0]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6">{flows[activeFlow].title}</h2>
        <div className="space-y-4">
          {flows[activeFlow].steps.map((step, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center font-bold">
                {idx + 1}
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Documents Checklist
          </h3>
          <ul className="space-y-2">
            {checklist.documents.map((doc, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>{doc}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-amber-600" />
            Questions to Ask Schools
          </h3>
          <ul className="space-y-2">
            {checklist.questions.map((q, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>{q}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ==================== EXAMS HUB PAGE ====================
export const ExamsHubPage = ({ setPage, goBack }) => {
  const exams = [
    {
      id: 'pep',
      name: 'PEP',
      fullName: 'Primary Exit Profile',
      who: 'Grade 6 students',
      when: 'March annually',
      color: 'bg-blue-500',
      desc: 'Determines secondary school placement'
    },
    {
      id: 'csec',
      name: 'CSEC',
      fullName: 'Caribbean Secondary Education Certificate',
      who: 'Form 5 students',
      when: 'May-June annually',
      color: 'bg-brand-500',
      desc: 'Standard secondary school leaving certification'
    },
    {
      id: 'cape',
      name: 'CAPE',
      fullName: 'Caribbean Advanced Proficiency Examination',
      who: 'Sixth Form students',
      when: 'May-June annually',
      color: 'bg-purple-500',
      desc: 'Advanced certification for university entry'
    },
    {
      id: 'nvqj',
      name: 'NVQ-J',
      fullName: 'National Vocational Qualification of Jamaica',
      who: 'TVET students',
      when: 'Ongoing',
      color: 'bg-orange-500',
      desc: 'Competency-based vocational certification'
    }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <FileText className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Exams & Certifications</h1>
        <p className="text-white/90">Everything you need to know about Jamaica's education exams</p>
      </div>

      <div className="grid gap-4 mb-8">
        {exams.map(exam => (
          <button
            key={exam.id}
            onClick={() => setPage(`exams-${exam.id}`)}
            className="bg-white rounded-xl p-6 text-left shadow-sm hover:shadow-md transition-all border border-gray-100 flex items-center gap-6"
          >
            <div className={`${exam.color} text-white px-4 py-2 rounded-lg font-black text-xl`}>
              {exam.name}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-800">{exam.fullName}</h3>
              <p className="text-sm text-gray-600">{exam.desc}</p>
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                <span><Users className="w-3 h-3 inline mr-1" />{exam.who}</span>
                <span><Calendar className="w-3 h-3 inline mr-1" />{exam.when}</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        ))}
      </div>

      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h2 className="font-bold text-gray-800 mb-4">Other Certifications</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setPage('exams-cityguilds')}
            className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-sky-300 hover:shadow-sm transition-all text-left"
          >
            <Globe className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-800">City & Guilds</h3>
              <p className="text-sm text-gray-600">International vocational qualifications</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
          </button>
          <button
            onClick={() => setPage('exams-sat')}
            className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-red-300 hover:shadow-sm transition-all text-left"
          >
            <Globe className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-800">SAT</h3>
              <p className="text-sm text-gray-600">For US university applications</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== PEP EXAM PAGE ====================
export const PEPExamPage = ({ setPage, goBack }) => (
  <div className="w-full">
    <BackButton goBack={goBack} />

    {/* Hurricane Melissa Disruption Notice
        This is a time-sensitive alert about changed 2026 exam components and
        dates, NOT a page hero — it deliberately keeps a warning treatment and
        is excluded from the brand-colour sweep. Reworked from a saturated
        amber/orange gradient with white text to dark text on a light amber
        field, which raises body-copy contrast from ~3:1 to ~11:1 while reading
        more clearly as a warning. */}
    <div role="alert" className="bg-amber-50 ring-1 ring-amber-300 rounded-2xl p-6 mb-6 text-amber-950 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="bg-amber-200/70 text-amber-800 p-3 rounded-xl flex-shrink-0">
          <AlertTriangle className="w-8 h-8" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-black mb-2">2026 PEP Changes Due to Hurricane Melissa</h2>
          <p className="text-amber-900 mb-4 leading-relaxed">
            The Ministry of Education has announced significant changes to the 2026 PEP examination
            to accommodate students impacted by Hurricane Melissa (Category 5, October 28, 2025).
          </p>

          <div className="bg-white ring-1 ring-amber-200 rounded-xl p-4 mb-4">
            <h3 className="font-bold mb-3">Key Changes for Grade 6 Students:</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold" aria-hidden="true">•</span>
                <span><strong>Reduced assessments:</strong> Only 3 components (Ability Test, Mathematics, Language Arts)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold" aria-hidden="true">•</span>
                <span><strong>Science & Social Studies removed</strong> from external assessment this year</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold" aria-hidden="true">•</span>
                <span><strong>New exam dates:</strong> Grade 6 - April 29-30 | Grade 5 - June 10 | Grade 4 - June 24</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold" aria-hidden="true">•</span>
                <span><strong>Modified curriculum:</strong> Revised grade level targets for Grades 1-9</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-wrap gap-3 text-sm">
            <a
              href="https://jis.gov.jm/changes-to-administration-of-pep-to-accommodate-students-impacted-by-hurricane-melissa/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center min-h-[44px] bg-amber-800 text-white hover:bg-amber-900 px-4 py-2 rounded-xl font-semibold transition-colors"
            >
              JIS Official Announcement →
            </a>
            <a
              href="https://moey.gov.jm/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center min-h-[44px] bg-white text-amber-900 ring-1 ring-amber-300 hover:bg-amber-100 px-4 py-2 rounded-xl font-semibold transition-colors"
            >
              MOEY Website →
            </a>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
      <div className="inline-block bg-white/20 px-4 py-2 rounded-lg font-black text-2xl mb-4">PEP</div>
      <h1 className="text-3xl font-black mb-2">Primary Exit Profile</h1>
      <p className="text-white/90">The exam that determines secondary school placement</p>
    </div>

    <div className="space-y-6">
      {/* Updated 2026 Exam Structure */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-xl font-bold text-gray-800">2026 Exam Structure</h2>
          <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded">UPDATED</span>
        </div>
        <p className="text-gray-600 mb-4">
          Due to Hurricane Melissa disruptions, the 2026 PEP was modified. Grade 6 students sat
          <strong> three assessments only</strong> on 29-30 April 2026 (31,806 candidates registered), and
          results were released on <strong>22 June 2026</strong>.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
            <h3 className="font-semibold text-blue-800">Ability Test</h3>
            <p className="text-sm text-blue-600 mb-2">40 multiple choice items</p>
            <p className="text-xs text-gray-500">Quantitative & verbal reasoning</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
            <h3 className="font-semibold text-blue-800">Mathematics</h3>
            <p className="text-sm text-blue-600 mb-2">60 multiple choice items</p>
            <p className="text-xs text-gray-500">Curriculum-based test</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
            <h3 className="font-semibold text-blue-800">Language Arts</h3>
            <p className="text-sm text-blue-600 mb-2">60 multiple choice items</p>
            <p className="text-xs text-gray-500">Curriculum-based test</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Note:</strong> Science and Social Studies have been removed from external assessment for 2026.
            The standard PEP structure will resume in subsequent years.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What is PEP?</h2>
        <p className="text-gray-600 mb-4">
          The Primary Exit Profile (PEP) replaced the Grade Six Achievement Test (GSAT) in 2019.
          It assesses students across multiple components to provide a more holistic view of their abilities.
        </p>
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
          <p className="text-sm text-amber-800">
            <strong>2026 Notice:</strong> Due to Hurricane Melissa, the exam schedule has been adjusted.
            See the updated structure above.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gray-100 rounded-lg p-4 opacity-60">
            <h3 className="font-semibold text-gray-600">Performance Task</h3>
            <p className="text-sm text-gray-500">Cancelled for 2026</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800">Ability Test</h3>
            <p className="text-sm text-blue-600">Grade 6 · 29-30 April 2026</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800">Curriculum-Based Tests</h3>
            <p className="text-sm text-blue-600">Mathematics &amp; Language Arts</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">How It's Graded</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-lg">
            <div className="w-8 h-8 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
            <div><span className="font-semibold">Highly Proficient</span> - Exceeds grade level expectations</div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
            <div><span className="font-semibold">Proficient</span> - Meets grade level expectations</div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
            <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
            <div><span className="font-semibold">Approaching</span> - Approaching grade level</div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
            <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold">4</div>
            <div><span className="font-semibold">Beginning</span> - Below grade level expectations</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Preparation Tips</h2>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
            <span>Practice with past papers (available from MOE website)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
            <span>Focus on reading comprehension and critical thinking</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
            <span>Get comfortable with the Performance Task format</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
            <span>Ensure adequate rest before exam days</span>
          </li>
        </ul>
      </div>

      <div className="bg-brand-50 rounded-xl p-6 border border-brand-200">
        <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-brand-600" />
          Find Extra Lessons & Prep Support
        </h2>
        <p className="text-gray-600 mb-4">Browse our directory of tutoring services and exam prep providers.</p>
        <button
          onClick={() => setPage('services')}
          className="bg-brand-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-brand-700 transition-colors"
        >
          Find Tutors
        </button>
      </div>
    </div>
  </div>
);

// CSEC Pass Rates Data (Source: MOEY 2025)
const csecPassRates2025 = {
  registrations: 30514,
  satRate: 93.9,
  passRates: [
    { year: 2025, subject: 'English A', pass_percent: 85, change: 9 },
    { year: 2025, subject: 'Mathematics', pass_percent: 44, change: 5 },
    { year: 2025, subject: 'Food Nutrition and Health', pass_percent: 93, change: null },
    { year: 2025, subject: 'Theatre Arts', pass_percent: 92, change: null },
    { year: 2025, subject: 'Principles of Business', pass_percent: 90, change: null },
    { year: 2025, subject: 'Information Technology', pass_percent: 88, change: null },
    { year: 2025, subject: 'Agricultural Science', pass_percent: 86, change: null },
    { year: 2025, subject: 'Biology', pass_percent: 84, change: null },
    { year: 2025, subject: 'Visual Arts', pass_percent: 83, change: null }
  ],
  regionalAverages: [
    { subject: 'English A', regional_pass_percent: 80 },
    { subject: 'Mathematics', regional_pass_percent: 39 }
  ],
  source: 'MOEY: Improved Performance in 2025 CSEC English and Maths',
  sourceUrl: 'https://moey.gov.jm/improved-performance-in-2025-csec-english-and-maths/'
};

// ==================== CSEC EXAM PAGE ====================
export const CSECExamPage = ({ setPage, goBack }) => (
  <div className="w-full">
    <BackButton goBack={goBack} />

    <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
      <div className="inline-block bg-white/20 px-4 py-2 rounded-lg font-black text-2xl mb-4">CSEC</div>
      <h1 className="text-3xl font-black mb-2">Caribbean Secondary Education Certificate</h1>
      <p className="text-white/90">The standard secondary school leaving certification</p>
    </div>

    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What is CSEC?</h2>
        <p className="text-gray-600 mb-4">
          CSEC is administered by the Caribbean Examinations Council (CXC) and is taken by students in Form 5
          (typically age 16-17). It's recognized across the Caribbean and internationally.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm font-medium">May-June Annually</span>
          <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm font-medium">Form 5 Students</span>
          <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm font-medium">Results in August</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Grading System</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { grade: 'I', desc: 'Distinction', color: 'bg-brand-500' },
            { grade: 'II', desc: 'Good', color: 'bg-blue-500' },
            { grade: 'III', desc: 'Acceptable', color: 'bg-purple-500' },
            { grade: 'IV', desc: 'Needs Improvement', color: 'bg-amber-500' },
            { grade: 'V', desc: 'Weak', color: 'bg-orange-500' },
            { grade: 'U', desc: 'Ungraded', color: 'bg-gray-500' }
          ].map(g => (
            <div key={g.grade} className="text-center p-3 bg-gray-50 rounded-lg">
              <div className={`${g.color} text-white w-10 h-10 rounded-full flex items-center justify-center font-bold mx-auto mb-2`}>{g.grade}</div>
              <div className="text-sm font-medium">{g.desc}</div>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-4">Grades I-III are considered passes. Many employers and institutions require Grade III or better in core subjects.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          All CSEC Subjects ({csecSubjects.length} subjects)
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(csecByCategory).map(([category, subjects]) => (
            <div key={category}>
              <h3 className="font-semibold text-gray-700 mb-2">{category}</h3>
              <ul className="space-y-1 text-sm text-gray-600">
                {subjects.map(s => (
                  <li key={s.subject}>{s.subject}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* 2025 Pass Rates Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-brand-600" />
          2025 National Pass Rates
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-brand-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-black text-brand-600">{csecPassRates2025.registrations.toLocaleString()}</div>
            <div className="text-xs text-gray-600">Students Registered</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-black text-blue-600">{csecPassRates2025.satRate}%</div>
            <div className="text-xs text-gray-600">Sat Rate</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-black text-purple-600">85%</div>
            <div className="text-xs text-gray-600">English A Pass</div>
          </div>
        </div>
        <div className="space-y-2">
          {csecPassRates2025.passRates.map((rate, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span className="font-medium text-gray-700">{rate.subject}</span>
              <div className="flex items-center gap-2">
                <span className={`font-bold ${rate.pass_percent >= 80 ? 'text-brand-600' : rate.pass_percent >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {rate.pass_percent}%
                </span>
                {rate.change && (
                  <span className="text-xs text-brand-600 bg-brand-100 px-2 py-0.5 rounded">
                    +{rate.change}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Source: <a href={csecPassRates2025.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{csecPassRates2025.source}</a>
        </p>
      </div>

      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-600" />
          Strategic Subject Selection
        </h2>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
            <span>Check subject requirements for your target career/university program</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
            <span>Balance sciences and humanities based on your strengths</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
            <span>Most students take 7-9 subjects; 5 passes minimum for Sixth Form</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

// ==================== CAPE EXAM PAGE ====================
export const CAPEExamPage = ({ setPage, goBack }) => (
  <div className="w-full">
    <BackButton goBack={goBack} />

    <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
      <div className="inline-block bg-white/20 px-4 py-2 rounded-lg font-black text-2xl mb-4">CAPE</div>
      <h1 className="text-3xl font-black mb-2">Caribbean Advanced Proficiency Examination</h1>
      <p className="text-white/90">Advanced certification for university entry</p>
    </div>

    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What is CAPE?</h2>
        <p className="text-gray-600 mb-4">
          CAPE is the advanced-level certification for sixth form students (Forms 6 Lower and Upper).
          It's equivalent to A-Levels and is required for university admission in the Caribbean.
        </p>
        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="font-semibold text-purple-800 mb-2">Unit System</h3>
          <p className="text-sm text-purple-700">
            Each subject has 2 Units. Unit 1 is taken in Lower Sixth, Unit 2 in Upper Sixth.
            Both units must be completed for the full qualification.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Entry Requirements</h2>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <span>Minimum 5 CSEC passes including English and Mathematics</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <span>Grade III or better in subjects related to intended CAPE subjects</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <span>Some schools require specific grades for competitive subjects</span>
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          All CAPE Subjects ({capeSubjects.length} subjects)
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          {Object.entries(capeByCategory).map(([category, subjects]) => (
            <div key={category}>
              <h3 className="font-semibold text-gray-700 mb-2">{category}</h3>
              <ul className="space-y-1 text-gray-600">
                {subjects.map(s => (
                  <li key={s.subject} className="flex justify-between items-center">
                    <span>{s.subject}</span>
                    <span className="text-xs text-purple-500 font-medium">
                      {s.units === 1 ? '1 Unit' : '2 Units'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ==================== NVQJ EXAM PAGE ====================
export const NVQJExamPage = ({ setPage, goBack }) => (
  <div className="w-full">
    <BackButton goBack={goBack} />

    <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
      <div className="inline-block bg-white/20 px-4 py-2 rounded-lg font-black text-2xl mb-4">NVQ-J</div>
      <h1 className="text-3xl font-black mb-2">National Vocational Qualification of Jamaica</h1>
      <p className="text-white/90">Competency-based vocational certification</p>
    </div>

    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What is NVQ-J?</h2>
        <p className="text-gray-600 mb-4">
          NVQ-J is Jamaica's competency-based qualification system for vocational and technical skills.
          It's administered by HEART/NSTA Trust and recognizes practical skills alongside academic knowledge.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">Competency-Based</span>
          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">Ongoing Assessment</span>
          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">Industry-Recognized</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Levels</h2>
        <div className="space-y-3">
          {[
            { level: 1, title: 'Entry Level', desc: 'Basic skills, supervised work' },
            { level: 2, title: 'Intermediate', desc: 'Routine tasks, some independence' },
            { level: 3, title: 'Advanced', desc: 'Complex tasks, can supervise others' },
            { level: 4, title: 'Specialist', desc: 'Technical expertise, management' },
            { level: 5, title: 'Professional', desc: 'Strategic planning, leadership' }
          ].map(l => (
            <div key={l.level} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold">{l.level}</div>
              <div>
                <div className="font-semibold text-gray-800">{l.title}</div>
                <div className="text-sm text-gray-600">{l.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Fields</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {['Automotive', 'Construction', 'Hospitality', 'Information Technology', 'Cosmetology', 'Agriculture', 'Healthcare', 'Electrical'].map(field => (
            <div key={field} className="p-3 bg-gray-50 rounded-lg text-center font-medium text-gray-700">{field}</div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ==================== SCHOOL DIRECTORY PAGE ====================
export const SchoolDirectoryPage = ({ setPage, goBack, initialParish = '' }) => {
  const [filters, setFilters] = useState({
    parish: initialParish,
    type: '',
    sector: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(!!initialParish);

  // Use static data from education-data.js
  // Combine with rankings data for richer information
  const allSchoolsWithRankings = staticSchoolsList.map(school => {
    const ranking = schoolRankings2024.find(r =>
      r.school_name.toLowerCase() === school.school_name.toLowerCase()
    );
    return {
      ...school,
      id: school.school_name,
      name: school.school_name,
      level: 'secondary',
      ownership: school.sector?.toLowerCase(),
      rank: ranking?.rank || null,
      score: ranking?.score || null,
      gender: ranking?.gender || null
    };
  });

  // Apply filters
  const filteredSchools = allSchoolsWithRankings.filter(school => {
    if (filters.parish && school.parish !== filters.parish) return false;
    if (filters.type && school.school_type !== filters.type) return false;
    if (filters.sector && school.sector !== filters.sector) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!school.name.toLowerCase().includes(searchLower) &&
          !school.parish.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    return true;
  });

  // Sort: ranked schools first (by rank), then alphabetically
  const schoolsList = [...filteredSchools].sort((a, b) => {
    if (a.rank && b.rank) return a.rank - b.rank;
    if (a.rank) return -1;
    if (b.rank) return 1;
    return a.name.localeCompare(b.name);
  });

  const loading = false; // Static data - no loading needed

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <School className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">School Directory</h1>
        <p className="text-white/90">Browse and compare schools across Jamaica</p>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search schools by name..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <Filter className="w-5 h-5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
            <select
              value={filters.parish}
              onChange={e => setFilters(f => ({ ...f, parish: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Parishes</option>
              {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={filters.type}
              onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Types</option>
              <option value="Secondary High">Secondary High</option>
              <option value="Technical High">Technical High</option>
            </select>
            <select
              value={filters.sector}
              onChange={e => setFilters(f => ({ ...f, sector: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Public & Independent</option>
              <option value="Public">Public</option>
              <option value="Independent">Independent</option>
            </select>
            <button
              onClick={() => setFilters({ parish: '', type: '', sector: '', search: '' })}
              className="px-3 py-2 text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-brand-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-black text-brand-600">{staticSchoolsList.length}</div>
          <div className="text-xs text-gray-600">Total Schools</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-black text-blue-600">{publicSchools.length}</div>
          <div className="text-xs text-gray-600">Public Schools</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-black text-purple-600">{independentSchools.length}</div>
          <div className="text-xs text-gray-600">Independent</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-black text-amber-600">{schoolRankings2024.length}</div>
          <div className="text-xs text-gray-600">Ranked Schools</div>
        </div>
      </div>

      {/* Student Success Tools */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Student Success Tools</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setPage('exam-tracker')}
            className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Calendar className="w-10 h-10 mb-3 opacity-90" />
            <h4 className="font-bold text-lg">Exam Tracker & Study Guide</h4>
            <p className="text-blue-100 text-sm mt-1">Track exam dates, access study resources, prepare for success</p>
          </button>
          <button
            onClick={() => setPage('extracurriculars')}
            className="bg-gradient-to-br from-cyan-500 to-brand-600 rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Star className="w-10 h-10 mb-3 opacity-90" />
            <h4 className="font-bold text-lg">Extracurricular Activities</h4>
            <p className="text-cyan-100 text-sm mt-1">Clubs, sports, and programs to build your profile</p>
          </button>
        </div>
      </div>

      {/* Earn & Learn Opportunities */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Earn & Learn Opportunities</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setPage('side-hustles')}
            className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <DollarSign className="w-8 h-8 mb-2 opacity-90" />
            <h4 className="font-bold mb-1">Student Side Hustles</h4>
            <p className="text-xs opacity-90">Safe, legal ways to earn while you learn</p>
          </button>
          <button
            onClick={() => setPage('summer-employment')}
            className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Briefcase className="w-8 h-8 mb-2 opacity-90" />
            <h4 className="font-bold mb-1">Summer Employment</h4>
            <p className="text-xs opacity-90">Find summer jobs, internships, and programs</p>
          </button>
          <button
            onClick={() => setPage('opportunity-matcher')}
            className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Zap className="w-8 h-8 mb-2 opacity-90" />
            <h4 className="font-bold mb-1">Opportunity Matcher</h4>
            <p className="text-xs opacity-90">Match your interests with jobs and internships</p>
          </button>
        </div>
      </div>

      {/* Quick Links for High School Students */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <h3 className="font-bold text-gray-800 mb-4">High School Resources</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => setPage('rankings')}
            className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-all text-left"
          >
            <Award className="w-6 h-6 text-amber-600" />
            <span className="font-medium text-gray-800 text-sm">Rankings</span>
          </button>
          <button
            onClick={() => setPage('exams-csec')}
            className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-all text-left"
          >
            <FileText className="w-6 h-6 text-brand-600" />
            <span className="font-medium text-gray-800 text-sm">CSEC Guide</span>
          </button>
          <button
            onClick={() => setPage('exams-cape')}
            className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-all text-left"
          >
            <GraduationCap className="w-6 h-6 text-purple-600" />
            <span className="font-medium text-gray-800 text-sm">CAPE Guide</span>
          </button>
          <button
            onClick={() => setPage('compare-schools')}
            className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-all text-left"
          >
            <Building2 className="w-6 h-6 text-blue-600" />
            <span className="font-medium text-gray-800 text-sm">Compare</span>
          </button>
        </div>
      </div>

      {/* Results */}
      {schoolsList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <School className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Schools Found</h3>
          <p className="text-gray-500">
            {filters.search || filters.parish || filters.type
              ? 'Try adjusting your filters or search terms.'
              : 'No schools match your criteria.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 font-medium">{schoolsList.length} schools found</p>
          {schoolsList.map((school, index) => (
            <div
              key={school.id}
              className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all border border-gray-100 flex items-center gap-4"
            >
              {/* Rank Badge or Index */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                school.rank ? (school.rank <= 10 ? 'bg-amber-100 text-amber-700' : 'bg-brand-100 text-brand-700') : 'bg-gray-100 text-gray-500'
              }`}>
                {school.rank ? `#${school.rank}` : index + 1}
              </div>

              {/* School Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-800">{school.name}</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />{school.parish}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    school.sector === 'Public' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {school.sector}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {school.school_type}
                  </span>
                </div>
              </div>

              {/* Score if available */}
              {school.score && (
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-brand-600">{school.score}</div>
                  <div className="text-xs text-gray-500">Score</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Data Source Note */}
      <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-500 text-center">
        Data source: Ministry of Education Directory & Educate Jamaica Performance Index 2024
      </div>
    </div>
  );
};

// ==================== SCHOOL PROFILE PAGE ====================
export const SchoolProfilePage = ({ schoolId, setPage, goBack }) => {
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSchool = async () => {
      setLoading(true);
      const { data } = await schools.getById(schoolId);
      setSchool(data);
      setLoading(false);
    };
    loadSchool();
  }, [schoolId]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mx-auto"></div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="text-center py-12">
        <School className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-600">School Not Found</h3>
        <button onClick={goBack} className="mt-4 text-brand-600 hover:underline">Go Back</button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="bg-gradient-to-br from-brand-600 to-brand-800 p-8 text-white">
          <h1 className="text-3xl font-black mb-2">{school.name}</h1>
          <div className="flex flex-wrap gap-3">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{school.parish}</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm capitalize">{school.level}</span>
            {school.type && <span className="bg-white/20 px-3 py-1 rounded-full text-sm capitalize">{school.type?.replace('_', ' ')}</span>}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3">Contact Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {school.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <span>{school.address}</span>
                </div>
              )}
              {school.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <a href={`tel:${school.phone}`} className="text-brand-600 hover:underline">{school.phone}</a>
                </div>
              )}
              {school.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <a href={`mailto:${school.email}`} className="text-brand-600 hover:underline">{school.email}</a>
                </div>
              )}
              {school.website && (
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">Website</a>
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3">School Details</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-500">Ownership</div>
                <div className="font-semibold capitalize">{school.ownership || 'N/A'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-500">Gender</div>
                <div className="font-semibold capitalize">{school.gender === 'coed' ? 'Co-Educational' : school.gender || 'N/A'}</div>
              </div>
              {school.founded && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Founded</div>
                  <div className="font-semibold">{school.founded}</div>
                </div>
              )}
              {school.principal && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Principal</div>
                  <div className="font-semibold">{school.principal}</div>
                </div>
              )}
            </div>
          </div>

          {/* Facilities */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3">Facilities & Support</h2>
            <div className="flex flex-wrap gap-2">
              {school.has_library && <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm">Library</span>}
              {school.has_computer_lab && <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm">Computer Lab</span>}
              {school.has_science_lab && <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm">Science Lab</span>}
              {school.has_sports_field && <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm">Sports Field</span>}
              {school.has_pool && <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm">Swimming Pool</span>}
              {school.has_auditorium && <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm">Auditorium</span>}
              {school.has_boarding && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Boarding</span>}
              {school.has_sixth_form && <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">Sixth Form</span>}
              {school.sen_support && <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm">SEN Support</span>}
              {school.shadow_allowed && <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm">Shadow Allowed</span>}
            </div>
          </div>

          {school.last_verified_at && (
            <div className="text-sm text-gray-500 pt-4 border-t">
              Last verified: {new Date(school.last_verified_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== RANKINGS HUB PAGE ====================
export const RankingsHubPage = ({ setPage, goBack }) => {
  const categories = [
    { id: 'academic', title: 'Academic Rankings', desc: 'Based on CSEC 5+ subject pass rates', icon: GraduationCap, color: 'bg-blue-500' },
    { id: 'extracurricular', title: 'Extracurricular', desc: 'Sports, quiz, and arts achievements', icon: Trophy, color: 'bg-amber-500' },
    { id: 'environment', title: 'Environment & Support', desc: 'Facilities, SEN support, safety', icon: Heart, color: 'bg-pink-500' },
    { id: 'popularity', title: 'Hot Schools', desc: 'Community votes and trends', icon: Zap, color: 'bg-purple-500' }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Trophy className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">School Rankings</h1>
        <p className="text-white/90">Compare schools across multiple dimensions</p>
      </div>

      {/* Important Disclaimer */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Understanding Rankings</p>
            <p>Rankings are aggregated indicators based on publicly available data for a specific year. They are <strong>not</strong> government endorsements, measures of student potential, or predictors of individual success. Small score differences are often statistically close.</p>
            <button onClick={() => setPage('how-guide-works')} className="text-blue-600 hover:underline mt-2 font-medium">
              Learn more about how this guide works
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 mb-8">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setPage(`rankings-${cat.id}`)}
            className="bg-white rounded-xl p-6 text-left shadow-sm hover:shadow-md transition-all border border-gray-100 flex items-center gap-6"
          >
            <div className={`${cat.color} text-white p-3 rounded-xl`}>
              <cat.icon className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-800 text-lg">{cat.title}</h3>
              <p className="text-sm text-gray-600">{cat.desc}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        ))}
      </div>

      <button
        onClick={() => setPage('rankings-methodology')}
        className="w-full bg-gray-50 rounded-xl p-4 text-left border border-gray-200 flex items-center gap-4 hover:bg-gray-100"
      >
        <Info className="w-6 h-6 text-gray-400" />
        <div>
          <h3 className="font-semibold text-gray-700">How Rankings Work</h3>
          <p className="text-sm text-gray-500">Our methodology, data sources, and important disclosures</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
      </button>
    </div>
  );
};

// ==================== ACADEMIC RANKINGS PAGE ====================
export const AcademicRankingsPage = ({ setPage, goBack }) => {
  const [selectedParish, setSelectedParish] = useState('');
  const [selectedGender, setSelectedGender] = useState('');

  // Filter rankings based on selections
  const filteredRankings = schoolRankings2024.filter(school => {
    if (selectedParish && !school.parish.includes(selectedParish)) return false;
    if (selectedGender && school.gender !== selectedGender) return false;
    return true;
  });

  const genderOptions = ['Girls Only', 'Boys Only', 'Co-educational'];
  const parishOptions = [...new Set(schoolRankings2024.map(s => s.parish))].sort();

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <GraduationCap className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Academic Rankings 2024</h1>
        <p className="text-white/90">Based on CSEC performance (5+ subjects incl. Maths &amp; English)</p>
        <p className="text-sm text-white/70 mt-2">Source: Educate Jamaica High School Performance Index 2024</p>
      </div>

      {/* 2024 index highlight */}
      <div className="mb-6 bg-brand-50 border border-brand-200 rounded-xl p-4">
        <h3 className="font-semibold text-brand-800 mb-1 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Now updated to the 2024 index
        </h3>
        <p className="text-sm text-brand-800">
          <strong>Campion College is #1 with a perfect 100%</strong>, overtaking Immaculate Conception
          (2nd, 99.18%). The table shows each school's 2024 score alongside its 2023 result and the change.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={selectedParish}
            onChange={e => setSelectedParish(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Parishes</option>
            {parishOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={selectedGender}
            onChange={e => setSelectedGender(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All School Types</option>
            {genderOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {(selectedParish || selectedGender) && (
            <button
              onClick={() => { setSelectedParish(''); setSelectedGender(''); }}
              className="px-3 py-2 text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Rankings Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="font-bold text-gray-800">
            {filteredRankings.length} Schools Ranked
          </h2>
        </div>
        <div className="divide-y">
          {filteredRankings.map((school, index) => (
            <div
              key={school.rank}
              className="p-4 flex items-center gap-4 hover:bg-gray-50"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                school.rank <= 3 ? 'bg-amber-500' :
                school.rank <= 10 ? 'bg-blue-500' :
                'bg-gray-400'
              }`}>
                {school.rank}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{school.school_name}</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {school.parish}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    school.gender === 'Girls Only' ? 'bg-pink-100 text-pink-700' :
                    school.gender === 'Boys Only' ? 'bg-blue-100 text-blue-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {school.gender}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-brand-600">{school.score}%</div>
                {school.trend === 'up' && (
                  <div className="text-xs font-semibold text-brand-600 flex items-center justify-end gap-0.5">
                    <ChevronUp className="w-3 h-3" />{school.change} vs 2023
                  </div>
                )}
                {school.trend === 'down' && (
                  <div className="text-xs font-semibold text-red-500 flex items-center justify-end gap-0.5">
                    <ChevronDown className="w-3 h-3" />{school.change} vs 2023
                  </div>
                )}
                {school.trend === 'flat' && (
                  <div className="text-xs text-gray-400">No change vs 2023</div>
                )}
                {school.trend === 'new' && (
                  <div className="text-xs font-semibold text-blue-500">New entry</div>
                )}
                {school.score_2023 != null && school.trend !== 'flat' && (
                  <div className="text-[11px] text-gray-400">2023: {school.score_2023}%</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Methodology Note */}
      <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-100">
        <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4" />
          About This Ranking
        </h3>
        <p className="text-sm text-blue-700">
          The performance score is the percentage of a school's total Grade 11 cohort that achieved
          5 or more CSEC subjects, inclusive of both Mathematics and English (counting subjects sat in
          Grades 7–10). Data source: Educate Jamaica High School Performance Index 2024.
        </p>
      </div>
    </div>
  );
};

// ==================== FUNDING PAGE ====================
export const FundingPage = ({ setPage, goBack }) => {
  const [activeTab, setActiveTab] = useState('scholarships');
  const [expandedItem, setExpandedItem] = useState(null);
  const [levelFilter, setLevelFilter] = useState('all');
  const [studyAbroadCountry, setStudyAbroadCountry] = useState('all');

  const tabs = [
    { id: 'scholarships', title: 'Local Scholarships', icon: Award },
    { id: 'study-abroad', title: 'Study Abroad', icon: Plane },
    { id: 'grants', title: 'Grants', icon: Banknote },
    { id: 'loans', title: 'Student Loans', icon: CreditCard },
    { id: 'jamvat', title: 'JAMVAT', icon: Target },
    { id: 'tips', title: 'Tips & Resources', icon: Lightbulb }
  ];

  const levelOptions = [
    { id: 'all', label: 'All Levels' },
    { id: 'secondary', label: 'Secondary' },
    { id: 'tertiary', label: 'Tertiary' },
    { id: 'postgraduate', label: 'Postgraduate' }
  ];

  const studyAbroadCountries = [
    { id: 'all', label: 'All Countries' },
    { id: 'united_kingdom', label: 'United Kingdom' },
    { id: 'united_states', label: 'United States' },
    { id: 'canada', label: 'Canada' },
    { id: 'china', label: 'China' },
    { id: 'japan', label: 'Japan' },
    { id: 'germany', label: 'Germany' },
    { id: 'australia', label: 'Australia' },
    { id: 'regional_caribbean', label: 'Caribbean' }
  ];

  // Filter scholarships by level
  const filteredScholarships = scholarshipsData.scholarships.filter(s => {
    if (levelFilter === 'all') return true;
    return s.level && s.level.includes(levelFilter);
  });

  // Get study abroad scholarships
  const getStudyAbroadScholarships = () => {
    if (studyAbroadCountry === 'all') {
      return Object.entries(scholarshipsData.study_abroad)
        .filter(([key]) => key !== 'tips_for_studying_abroad' && key !== 'important_resources')
        .flatMap(([country, scholarships]) =>
          Array.isArray(scholarships) ? scholarships.map(s => ({ ...s, country })) : []
        );
    }
    return (scholarshipsData.study_abroad[studyAbroadCountry] || []).map(s => ({ ...s, country: studyAbroadCountry }));
  };

  const ScholarshipCard = ({ scholarship, isExpanded, onToggle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
      <button
        onClick={onToggle}
        className="w-full p-5 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-gray-800">{scholarship.name}</h3>
            {scholarship.type === 'merit' && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Merit</span>
            )}
            {scholarship.type === 'need-based' && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Need-based</span>
            )}
          </div>
          <p className="text-sm text-gray-600">{scholarship.provider}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-brand-600 font-semibold text-sm">{scholarship.amount}</span>
            {scholarship.deadline && (
              <span className="text-gray-500 text-xs flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {scholarship.deadline}
              </span>
            )}
          </div>
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Eligibility</h4>
              <ul className="space-y-1">
                {scholarship.eligibility?.map((req, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Fields of Study</h4>
              <div className="flex flex-wrap gap-1 mb-4">
                {scholarship.fields?.map((field, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                    {field}
                  </span>
                ))}
              </div>
              <h4 className="font-semibold text-gray-700 text-sm mb-2">How to Apply</h4>
              <p className="text-sm text-gray-600">{scholarship.how_to_apply}</p>
              {scholarship.website && (
                <a
                  href={scholarship.website.startsWith('http') ? scholarship.website : `https://${scholarship.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 text-sm mt-2 hover:underline"
                >
                  <Globe className="w-4 h-4" /> Visit Website
                </a>
              )}
            </div>
          </div>
          {scholarship.renewable && (
            <div className="mt-4 p-3 bg-brand-50 rounded-lg">
              <p className="text-sm text-brand-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <strong>Renewable:</strong> This scholarship can be renewed each year
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const StudyAbroadCard = ({ scholarship, isExpanded, onToggle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
      <button
        onClick={onToggle}
        className="w-full p-5 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-gray-800">{scholarship.name}</h3>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
              {scholarship.country?.replace('_', ' ')}
            </span>
          </div>
          <p className="text-sm text-gray-600">{scholarship.provider}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-sm text-gray-500">{scholarship.level}</span>
            {scholarship.duration && (
              <span className="text-gray-500 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" /> {scholarship.duration}
              </span>
            )}
          </div>
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-700 text-sm mb-2">What's Covered</h4>
              <ul className="space-y-1">
                {scholarship.coverage?.map((item, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Eligibility</h4>
              <ul className="space-y-1">
                {scholarship.eligibility?.map((req, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-blue-800 text-sm mb-1">Deadline</h4>
                <p className="text-sm text-blue-700">{scholarship.deadline}</p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 text-sm mb-1">How to Apply</h4>
                <p className="text-sm text-blue-700">{scholarship.how_to_apply}</p>
              </div>
            </div>
            {scholarship.website && (
              <a
                href={scholarship.website.startsWith('http') ? scholarship.website : `https://${scholarship.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 text-sm mt-3 hover:underline font-medium"
              >
                <Globe className="w-4 h-4" /> Official Website <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {scholarship.notes && scholarship.notes.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Important Notes</h4>
              <ul className="space-y-1">
                {scholarship.notes.map((note, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const GrantCard = ({ grant }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-bold text-gray-800">{grant.name}</h3>
          <p className="text-sm text-gray-600">{grant.provider}</p>
        </div>
        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
          {grant.amount}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-3">{grant.description}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {grant.level?.map((level, idx) => (
          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded capitalize">
            {level}
          </span>
        ))}
      </div>
      <h4 className="font-semibold text-gray-700 text-sm mb-2">Eligibility</h4>
      <ul className="space-y-1 mb-3">
        {grant.eligibility?.map((req, idx) => (
          <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
            {req}
          </li>
        ))}
      </ul>
      <p className="text-sm text-purple-600 font-medium">{grant.how_to_apply}</p>
    </div>
  );

  const LoanCard = ({ loan }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-bold text-gray-800">{loan.name}</h3>
          <p className="text-sm text-gray-600">{loan.provider}</p>
        </div>
        <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
          {loan.amount}
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4 p-4 bg-orange-50 rounded-lg">
        <div>
          <p className="text-xs text-orange-600 uppercase font-medium">Interest Rate</p>
          <p className="text-lg font-bold text-orange-800">{loan.interest_rate}</p>
        </div>
        <div>
          <p className="text-xs text-orange-600 uppercase font-medium">Repayment</p>
          <p className="text-sm font-medium text-orange-800">{loan.repayment}</p>
        </div>
      </div>
      <h4 className="font-semibold text-gray-700 text-sm mb-2">Eligibility</h4>
      <ul className="space-y-1 mb-3">
        {loan.eligibility?.map((req, idx) => (
          <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            {req}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{loan.how_to_apply}</p>
        {loan.website && (
          <a
            href={loan.website.startsWith('http') ? loan.website : `https://${loan.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-orange-600 text-sm hover:underline"
          >
            <Globe className="w-4 h-4" /> Apply
          </a>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <DollarSign className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Scholarships & Funding</h1>
        <p className="text-white/90">Find financial support for education at every level - local and international</p>
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="px-3 py-1 bg-white/20 rounded-full text-sm">
            {scholarshipsData.scholarships.length} Local Scholarships
          </div>
          <div className="px-3 py-1 bg-white/20 rounded-full text-sm">
            {Object.values(scholarshipsData.study_abroad).filter(Array.isArray).flat().length}+ Study Abroad Options
          </div>
          <div className="px-3 py-1 bg-white/20 rounded-full text-sm">
            {scholarshipsData.grants.length} Grants
          </div>
          <div className="px-3 py-1 bg-white/20 rounded-full text-sm">
            {scholarshipsData.loans.length} Loan Options
          </div>
        </div>
      </div>

      {/* Tab Navigation
          Restyle: each tab used to paint itself a different colour when
          active — green, blue, purple, orange, teal, amber — hard-coded as an
          inline `style` that also beat the Tailwind class beside it. Six
          accent colours in one control strip is noise, not wayfinding; the
          active tab is now simply the brand colour. */}
      <div className="flex overflow-x-auto gap-2 mb-6 pb-2" role="tablist">
        {tabs.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 min-h-[44px] rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-150 active:scale-[0.97] ${
              activeTab === tab.id
                ? 'bg-brand-600 text-white shadow-sm'
                : 'bg-canvas-sunken text-ink-muted ring-1 ring-hairline hover:bg-white hover:text-ink'
            }`}
          >
            <tab.icon className="w-4 h-4" aria-hidden="true" />
            {tab.title}
          </button>
        ))}
      </div>

      {/* Local Scholarships Tab */}
      {activeTab === 'scholarships' && (
        <div>
          <div className="flex items-center gap-4 mb-6">
            <label className="text-sm font-medium text-gray-700">Filter by Level:</label>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {levelOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-4 text-sm text-gray-600">
            Showing {filteredScholarships.length} scholarship{filteredScholarships.length !== 1 ? 's' : ''}
          </div>

          <ShowMoreList items={filteredScholarships} initial={5} noun="scholarships">
            {(scholarship) => (
              <ScholarshipCard
                key={scholarship.id}
                scholarship={scholarship}
                isExpanded={expandedItem === scholarship.id}
                onToggle={() => setExpandedItem(expandedItem === scholarship.id ? null : scholarship.id)}
              />
            )}
          </ShowMoreList>
        </div>
      )}

      {/* Study Abroad Tab */}
      {activeTab === 'study-abroad' && (
        <div>
          <div className="bg-blue-50 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
              <Plane className="w-5 h-5" /> Study Abroad Opportunities for Jamaicans
            </h3>
            <p className="text-sm text-blue-700">
              These scholarships are specifically available to Jamaican citizens. Many fully fund your studies including tuition, living expenses, and airfare.
            </p>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <label className="text-sm font-medium text-gray-700">Filter by Country:</label>
            <select
              value={studyAbroadCountry}
              onChange={(e) => setStudyAbroadCountry(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {studyAbroadCountries.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          <ShowMoreList items={getStudyAbroadScholarships()} initial={5} noun="scholarships">
            {(scholarship) => (
              <StudyAbroadCard
                key={scholarship.id}
                scholarship={scholarship}
                isExpanded={expandedItem === scholarship.id}
                onToggle={() => setExpandedItem(expandedItem === scholarship.id ? null : scholarship.id)}
              />
            )}
          </ShowMoreList>

          {/* Study Abroad Tips */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-6 text-white mt-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" /> Tips for Studying Abroad
            </h3>
            <ul className="space-y-2">
              {scholarshipsData.study_abroad.tips_for_studying_abroad?.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-blue-100">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-200" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Important Resources */}
          {scholarshipsData.study_abroad.important_resources && (
            <div className="mt-6">
              <h3 className="font-bold text-gray-800 mb-4">Important Resources</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(scholarshipsData.study_abroad.important_resources).map(([key, resource]) => (
                  <div key={key} className="bg-white rounded-xl p-4 border border-gray-100">
                    <h4 className="font-semibold text-gray-800">{resource.name}</h4>
                    <p className="text-sm text-gray-600 mb-2">{resource.services}</p>
                    {resource.website && (
                      <a
                        href={resource.website.startsWith('http') ? resource.website : `https://${resource.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm hover:underline flex items-center gap-1"
                      >
                        <Globe className="w-4 h-4" /> Visit Website
                      </a>
                    )}
                    {resource.address && (
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" /> {resource.address}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grants Tab */}
      {activeTab === 'grants' && (
        <div>
          <div className="bg-purple-50 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-purple-800 mb-2">Education Grants</h3>
            <p className="text-sm text-purple-700">
              Grants do not need to be repaid. These are primarily government programmes to support students from low-income families.
            </p>
          </div>

          {scholarshipsData.grants.map(grant => (
            <GrantCard key={grant.id} grant={grant} />
          ))}
        </div>
      )}

      {/* Loans Tab */}
      {activeTab === 'loans' && (
        <div>
          <div className="bg-orange-50 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-orange-800 mb-2">Student Loans</h3>
            <p className="text-sm text-orange-700">
              Loans must be repaid after graduation. The Students' Loan Bureau (SLB) offers the most affordable rates for Jamaican students.
            </p>
          </div>

          {scholarshipsData.loans.map(loan => (
            <LoanCard key={loan.id} loan={loan} />
          ))}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-6">
            <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Before Taking a Loan
            </h4>
            <ul className="space-y-2 text-sm text-amber-700">
              <li>• Apply for scholarships and grants first - they don't need to be repaid</li>
              <li>• Calculate the total cost including interest before committing</li>
              <li>• SLB loans have lower interest rates than commercial bank loans</li>
              <li>• Ensure you have a guarantor who understands their obligations</li>
              <li>• Start repayment planning before graduation</li>
            </ul>
          </div>
        </div>
      )}

      {/* JAMVAT Tab */}
      {activeTab === 'jamvat' && (
        <div>
          <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl p-6 text-white mb-6">
            <Target className="w-10 h-10 mb-3" />
            <h2 className="text-2xl font-bold mb-2">{scholarshipsData.jamvat.name}</h2>
            <p className="text-brand-100 mb-4">{scholarshipsData.jamvat.description}</p>
            <div className="text-3xl font-black">{scholarshipsData.jamvat.amount}</div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4">Eligibility Requirements</h3>
              <ul className="space-y-2">
                {scholarshipsData.jamvat.eligibility?.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4">How to Apply</h3>
              <p className="text-sm text-gray-600 mb-4">{scholarshipsData.jamvat.how_to_apply}</p>
              <a
                href={`https://${scholarshipsData.jamvat.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Globe className="w-4 h-4" /> Register at {scholarshipsData.jamvat.website}
              </a>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 mt-6">
            <h3 className="font-bold text-gray-800 mb-4">Approved Institutions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {scholarshipsData.jamvat.approved_institutions?.map((inst, idx) => (
                <div key={idx} className="px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded-lg">
                  {inst}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-6">
            <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Important Notes
            </h4>
            <ul className="space-y-2">
              {scholarshipsData.jamvat.important_notes?.map((note, idx) => (
                <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tips Tab */}
      {activeTab === 'tips' && (
        <div>
          <div className="bg-amber-50 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" /> Scholarship Success Tips
            </h3>
            <p className="text-sm text-amber-700">
              Follow these tips to maximize your chances of securing funding for your education.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" /> General Tips
              </h3>
              <ul className="space-y-3">
                {scholarshipsData.tips.general?.map((tip, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <School className="w-5 h-5 text-blue-500" /> For Secondary Students
              </h3>
              <ul className="space-y-3">
                {scholarshipsData.tips.for_secondary?.map((tip, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-500" /> For Tertiary Students
              </h3>
              <ul className="space-y-3">
                {scholarshipsData.tips.for_tertiary?.map((tip, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Quick Action */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white mt-6">
            <h3 className="text-xl font-bold mb-2">Need Help Finding Funding?</h3>
            <p className="text-slate-300 mb-4">
              Visit your school's guidance counselor or the Ministry of Education for personalized scholarship assistance.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://moey.gov.jm"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white text-slate-800 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                <Globe className="w-4 h-4" /> Ministry of Education
              </a>
              <a
                href="https://slb.gov.jm"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700 transition-colors flex items-center gap-2"
              >
                <Globe className="w-4 h-4" /> Students' Loan Bureau
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// The jobs board moved to src/components/EducationJobsPage.jsx when it gained
// a submission form, a poster's own-listings view and moderation state.

// ==================== HELP/FAQ PAGE ====================
export const HelpPage = ({ setPage, goBack }) => {
  const [activeCategory, setActiveCategory] = useState('choosing');

  const categories = {
    choosing: {
      title: 'Choosing a School',
      faqs: [
        { q: 'What should I consider when choosing a primary school?', a: 'Consider location, class sizes, curriculum approach, facilities, extra-curricular activities, and how well the school fits your child\'s learning style.' },
        { q: 'How important are rankings?', a: 'Rankings provide one data point but shouldn\'t be the only factor. Consider the school\'s culture, your child\'s needs, and practical factors like commute time.' },
        { q: 'Can I tour schools before applying?', a: 'Yes! Most schools welcome parent visits. Call ahead to schedule a tour during the registration period.' }
      ]
    },
    exams: {
      title: 'Understanding Exams',
      faqs: [
        { q: 'What is the difference between PEP and GSAT?', a: 'PEP replaced GSAT in 2019. PEP includes three components (Performance Task, Ability Test, Curriculum Test) and assesses critical thinking more than rote memorization.' },
        { q: 'How many CSEC subjects should my child take?', a: 'Most students take 7-9 subjects. A minimum of 5 passes (including English and Math) is typically required for Sixth Form entry.' },
        { q: 'When are exam results released?', a: 'PEP results come in May. CSEC and CAPE results are typically released in August.' }
      ]
    },
    transfers: {
      title: 'Transfers',
      faqs: [
        { q: 'Can I transfer my child to a different school?', a: 'Yes, but transfers require approval from both schools and sometimes the Ministry. Valid reasons include relocation, academic needs, or safety concerns.' },
        { q: 'When is the best time to transfer?', a: 'Beginning of a school year or term is ideal. Mid-year transfers can be disruptive but are possible with proper documentation.' }
      ]
    },
    support: {
      title: 'Special Support',
      faqs: [
        { q: 'How do I get exam accommodations for my child?', a: 'Request an assessment through the school or MOE. Documentation from a psychologist or medical professional is usually required. Apply early - accommodations must be approved before exams.' },
        { q: 'What is a shadow teacher?', a: 'A shadow is a dedicated support person who assists a child with special needs in the classroom. Some schools allow shadows; check individual school policies.' }
      ]
    },
    rankings: {
      title: 'How Rankings Work',
      faqs: [
        { q: 'Do paid listings affect school rankings?', a: 'No. Paid listings are completely separate from academic rankings. They only affect visibility in the Service Provider marketplace. Rankings are based solely on publicly available performance data.' },
        { q: 'What data sources are used?', a: 'We use official Ministry of Education data, CXC examination results, and verified public records. All data sources and update dates are disclosed. When data is unavailable, we say so clearly.' },
        { q: 'How are hurricane-affected schools handled?', a: 'Schools affected by hurricanes or other disruptions receive contextual flags. Academic outcomes during disrupted periods should be interpreted with care. We avoid unfair comparisons and always provide context.' },
        { q: 'Are rankings government endorsements?', a: 'No. Rankings are aggregated indicators based on publicly available data for a specific year. They are not government endorsements, measures of student potential, or predictors of individual success.' },
        { q: 'What if score differences are small?', a: 'Small differences in scores (less than 3%) are often statistically close and should not be over-interpreted. We encourage families to look beyond rankings at factors like subject strengths, programmes, and school culture.' }
      ]
    }
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <HelpCircle className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Help & FAQs</h1>
        <p className="text-white/90">Find answers to common questions about Jamaica's education system</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(categories).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === key
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {cat.title}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">{categories[activeCategory].title}</h2>
        <div className="space-y-6">
          {categories[activeCategory].faqs.map((faq, idx) => (
            <div key={idx} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
              <h3 className="font-semibold text-gray-800 mb-2">{faq.q}</h3>
              <p className="text-gray-600">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How This Guide Works Link */}
      <button
        onClick={() => setPage('how-guide-works')}
        className="w-full mt-6 bg-brand-50 border border-brand-200 rounded-xl p-4 text-left flex items-center gap-4 hover:bg-brand-100 transition-colors"
      >
        <Info className="w-6 h-6 text-brand-600" />
        <div>
          <h3 className="font-semibold text-brand-800">How This Guide Works</h3>
          <p className="text-sm text-brand-600">Learn about our data sources, methodology, and commitment to accuracy</p>
        </div>
        <ChevronRight className="w-5 h-5 text-brand-400 ml-auto" />
      </button>
    </div>
  );
};
